import React, { useMemo } from 'react';
import { Calendar, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

interface DateRangeSelectorProps {
  selectedDates: string[];
  onDatesChange: (dates: string[]) => void;
  minDate?: string;
  maxDate?: string;
  disabled?: boolean;
  excludeWeekends?: boolean;
  occupiedDates?: string[];
}

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
  selectedDates,
  onDatesChange,
  minDate,
  maxDate,
  disabled = false,
  excludeWeekends = true,
  occupiedDates = []
}) => {
  const [currentMonth, setCurrentMonth] = React.useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });

  const daysInMonth = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const days = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();

    return { days, firstDay, year, month };
  }, [currentMonth]);

  const isDateDisabled = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];

    if (excludeWeekends && (date.getDay() === 0 || date.getDay() === 6)) return true;
    if (minDate && dateStr < minDate) return true;
    if (maxDate && dateStr > maxDate) return true;

    return false;
  };

  const isDateSelected = (dateStr: string) => {
    return selectedDates.includes(dateStr);
  };

  const isDateOccupied = (dateStr: string) => {
    return occupiedDates.includes(dateStr);
  };

  const toggleDate = (dateStr: string) => {
    if (disabled) return;

    if (isDateSelected(dateStr)) {
      onDatesChange(selectedDates.filter(d => d !== dateStr));
    } else {
      onDatesChange([...selectedDates, dateStr].sort());
    }
  };

  const removeDate = (dateStr: string) => {
    onDatesChange(selectedDates.filter(d => d !== dateStr));
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const formatSelectedDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  const dayNames = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

  return (
    <div className="space-y-4">
      <div className="bg-gray-750 rounded-lg border border-gray-600 p-4">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={handlePrevMonth}
            disabled={disabled}
            className="p-2 hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-5 w-5 text-gray-300" />
          </button>
          <h3 className="text-base font-semibold text-white">
            {monthNames[daysInMonth.month]} {daysInMonth.year}
          </h3>
          <button
            onClick={handleNextMonth}
            disabled={disabled}
            className="p-2 hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="h-5 w-5 text-gray-300" />
          </button>
        </div>

        {occupiedDates.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-3 text-xs text-gray-400">
            <div className="flex items-center space-x-1.5">
              <div className="w-4 h-4 bg-gray-700 border-2 border-transparent rounded flex items-center justify-center">
                <div className="w-1 h-1 bg-yellow-500 rounded-full"></div>
              </div>
              <span>SDR occupé</span>
            </div>
            <div className="flex items-center space-x-1.5">
              <div className="w-4 h-4 bg-blue-600 border-2 border-blue-500 rounded"></div>
              <span>Sélectionné</span>
            </div>
            <div className="flex items-center space-x-1.5">
              <div className="w-4 h-4 bg-gray-700 border-2 border-transparent rounded ring-2 ring-green-500"></div>
              <span>Aujourd'hui</span>
            </div>
          </div>
        )}

        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map(day => (
            <div key={day} className="text-center text-xs font-medium text-gray-400 py-1">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: daysInMonth.firstDay }).map((_, i) => (
            <div key={`empty-${i}`} />
          ))}

          {Array.from({ length: daysInMonth.days }).map((_, i) => {
            const day = i + 1;
            const date = new Date(daysInMonth.year, daysInMonth.month, day);
            const dateStr = `${daysInMonth.year}-${String(daysInMonth.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const isDisabled = isDateDisabled(date);
            const isSelected = isDateSelected(dateStr);
            const isOccupied = isDateOccupied(dateStr);
            const isToday = new Date().toDateString() === date.toDateString();

            return (
              <motion.button
                key={day}
                whileHover={!isDisabled && !disabled ? { scale: 1.1 } : {}}
                whileTap={!isDisabled && !disabled ? { scale: 0.95 } : {}}
                onClick={() => !isDisabled && toggleDate(dateStr)}
                disabled={isDisabled || disabled}
                className={`
                  aspect-square rounded-lg text-sm font-medium transition-all relative
                  ${isSelected
                    ? 'bg-blue-600 text-white border-2 border-blue-500'
                    : isDisabled
                      ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600 border-2 border-transparent'
                  }
                  ${isToday && !isSelected ? 'ring-2 ring-green-500' : ''}
                  ${isOccupied && !isSelected ? 'ring-2 ring-yellow-500' : ''}
                `}
              >
                {day}
                {isOccupied && !isSelected && (
                  <div className="absolute bottom-0.5 left-1/2 transform -translate-x-1/2">
                    <div className="w-1 h-1 bg-yellow-500 rounded-full"></div>
                  </div>
                )}
              </motion.button>
            );
          })}
        </div>
      </div>

      {selectedDates.length > 0 && (
        <div className="bg-gray-750 rounded-lg border border-gray-600 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <Calendar className="h-4 w-4 text-blue-400" />
              <h4 className="text-sm font-semibold text-white">
                Dates sélectionnées ({selectedDates.length})
              </h4>
            </div>
            {!disabled && (
              <button
                onClick={() => onDatesChange([])}
                className="text-xs text-red-400 hover:text-red-300 transition-colors"
              >
                Tout effacer
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
            <AnimatePresence>
              {selectedDates.map(dateStr => (
                <motion.div
                  key={dateStr}
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.8, opacity: 0 }}
                  className="flex items-center space-x-1 px-3 py-1.5 bg-blue-600 rounded-lg text-white text-xs font-medium"
                >
                  <span>{formatSelectedDate(dateStr)}</span>
                  {!disabled && (
                    <button
                      onClick={() => removeDate(dateStr)}
                      className="ml-1 hover:bg-blue-700 rounded-full p-0.5 transition-colors"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeSelector;
